"use client";
import React, { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/card-shadcn";
import { Button } from "@/components/ui/ButtonShadcn";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import LoginForm from "@/components/partials/auth/login-form";
import RegForm from "@/components/partials/auth/reg-from";
import UploadVerification from "@/components/ui/UploadVerification";
import useAuthInfo from "@/store/auth";
import { AjaxGetAnon, AjaxPost } from "@/util/Ajax";
import Swal from "sweetalert2";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";

const LoanCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(500);
  const [paydayDate, setPaydayDate] = useState(null);
  const [currentView, setCurrentView] = useState("calculator"); // 'calculator', 'auth', 'wizard'
  const [authMode, setAuthMode] = useState("login"); // 'login' or 'register'
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadedDocuments, setUploadedDocuments] = useState({});
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingApplication, setIsCreatingApplication] = useState(false);

  const authData = useAuthInfo((state) => state);

  // Fixed rates as per requirements
  const raisingFeeRate = 5; // 5% raising fee
  const interestRate = 5; // 5% interest
  const initiationFeeRate = 12; // 12% initiation fee

  // Helper function to calculate days between today and payday
  const calculateDaysToPayday = () => {
    if (!paydayDate) return 1;
    const today = new Date();
    const payDay = new Date(paydayDate);
    const diffTime = payDay.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(1, Math.min(30, diffDays)); // Ensure between 1-30 days
  };

  // Calculate loan details based on new requirements
  const calculateLoan = () => {
    const principal = parseFloat(loanAmount) || 0;
    const loanDays = calculateDaysToPayday();

    // Calculate fees according to requirements
    const raisingFee = principal * (raisingFeeRate / 100); // 5% raising fee
    const amountReceived = principal - raisingFee; // Amount user receives after raising fee
    const interest = principal * (interestRate / 100) * (loanDays / 30); // 5% interest based on days (loan amount × 5% × days/30)
    const initiationFee = principal * (initiationFeeRate / 100); // 12% initiation fee
    const totalPayback = principal + interest + initiationFee; // Total to pay back

    return {
      principal,
      raisingFee,
      amountReceived,
      interest,
      initiationFee,
      totalPayback,
      days: loanDays,
    };
  };

  const loanDetails = calculateLoan();
  const isAmountValid = loanAmount >= 500 && loanAmount <= 2000;

  // Function to ask compliance questions
  const askComplianceQuestions = async () => {
    // Question 1: Administration / Debt Review Status
    const question1 = await Swal.fire({
      title: "Administration / Debt Review Status (1/4)",
      html: "Are you currently under debt review, administration, or any formal debt restructuring process?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      confirmButtonColor: "#4669fa",
      cancelButtonColor: "#d33",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    if (question1.isConfirmed) {
      await Swal.fire({
        title: "Application Cannot Proceed ",
        text: "Unfortunately, we cannot process your application at this time as you are currently under debt review, administration, or formal debt restructuring.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#4669fa",
      });
      return false;
    }

    // Question 2: Blacklisting / Credit Impairment Check
    const question2 = await Swal.fire({
      title: "Blacklisting / Credit Impairment Check (2/4)" ,
      html: "Have you been listed with any credit bureau for defaults, judgments, or adverse credit records?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      confirmButtonColor: "#4669fa",
      cancelButtonColor: "#d33",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    if (question2.isConfirmed) {
      await Swal.fire({
        title: "Application Cannot Proceed",
        text: "Unfortunately, we cannot process your application at this time due to adverse credit records.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#4669fa",
      });
      return false;
    }

    // Question 3: Consent to Process Personal Information
    const question3 = await Swal.fire({
      title: "Consent to Process Personal Information (3/4)",
      html: "Do you consent to us verifying your details and processing your personal and financial information for the purpose of assessing your loan application?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      confirmButtonColor: "#4669fa",
      cancelButtonColor: "#d33",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    if (!question3.isConfirmed) {
      await Swal.fire({
        title: "Application Cannot Proceed",
        text: "We require your consent to process your personal and financial information to assess your loan application.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#4669fa",
      });
      return false;
    }

    // Question 4: Accuracy & Fraud Declaration
    const question4 = await Swal.fire({
      title: "Accuracy & Fraud Declaration (4/4)",
      html: "Do you confirm that all information you provide is truthful, complete, and accurate, and understand that providing false or fraudulent information may result in your application being declined and may be reported to the relevant authorities?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      confirmButtonColor: "#4669fa",
      cancelButtonColor: "#d33",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    if (!question4.isConfirmed) {
      await Swal.fire({
        title: "Application Cannot Proceed",
        text: "You must confirm that all information provided is truthful, complete, and accurate to proceed with your application.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#4669fa",
      });
      return false;
    }

    // All questions passed
    return true;
  };

  // Consolidated function to create application and proceed
  const createApplicationAndProceed = async () => {
    setIsCreatingApplication(true);

    try {
      // Ask compliance questions first
      const compliancePassed = await askComplianceQuestions();
      
      if (!compliancePassed) {
        setIsCreatingApplication(false);
        return;
      }

      // Store loan application data in localStorage
      const loanApplicationData = {
        loanAmount: loanAmount,
        paydayDate: paydayDate ? paydayDate.toISOString() : null,
        days: loanDetails.days,
        loanDetails: loanDetails,
        loanType: "payday", // Fixed to payday loan
        applicationDate: new Date().toISOString(),
        userId: authData.userId,
      };

      localStorage.setItem(
        "currentApplication",
        JSON.stringify({
          loanDetails: loanDetails,
        })
      );

      // Create Application record with PascalCase for C# backend
      try {
        const applicationResponse = await AjaxPost(
          authData,
          "Application/Create",
          {
            Amount: loanDetails.principal,
            AmountReceive: loanDetails.amountReceived,
            Days: loanDetails.days,
          }
        );

        if (applicationResponse && applicationResponse.data) {
          const applicationId = applicationResponse.data;
          console.log(
            "Application created successfully with ID:",
            applicationId
          );

          // Store applicationId for use throughout the journey
          localStorage.setItem("applicationId", applicationId.toString());

          // Log the initial step
          try {
            await AjaxPost(authData, "Application/Log", {
              ApplicationId: applicationId,
              Step: "APPLICATION_CREATED_FROM_LOAN_CALCULATOR",
              Data: JSON.stringify({
                timestamp: new Date().toISOString(),
                loanDetails: loanDetails,
                source: "loan_calculator",
                userAgent: navigator.userAgent,
              }),
            });
            console.log("Initial application step logged");
          } catch (logError) {
            console.log(
              "Failed to log initial step, continuing anyway:",
              logError
            );
          }
        }
      } catch (error) {
        console.error("Application/Create error:", error);
        // Continue without application tracking if endpoints not available
        if (error.response?.status === 404) {
          console.log(
            "Application endpoints not deployed, continuing without tracking"
          );
          await Swal.fire({
            title: "Note",
            text: "Application tracking is currently unavailable. Your loan application will still be processed.",
            icon: "info",
            confirmButtonText: "Continue",
            confirmButtonColor: "#4669fa",
          });
        }
      }

      // Store the full loan application data for later use
      localStorage.setItem(
        "loan_application",
        JSON.stringify(loanApplicationData)
      );

      // Navigate to gathr onboarding regardless of Application creation success
      window.location.href = "/gathr/onboarding";
    } catch (error) {
      console.error("Error in loan application:", error);
      await Swal.fire({
        title: "Error",
        text: "An error occurred while starting your application. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#4669fa",
      });
    } finally {
      setIsCreatingApplication(false);
    }
  };

  const handleBorrowToday = async () => {
    // Check if user is logged in
    if (!authData.apiToken || !authData.userId) {
      setCurrentView("auth");
      return;
    }

    // User is logged in, create application and proceed
    await createApplicationAndProceed();
  };

  const handleAuthSuccess = async () => {
    // User just authenticated, create application and proceed
    await createApplicationAndProceed();
  };

  // Authentication View
  if (currentView === "auth") {
    return (
      <div className="w-full  mx-auto p-4 sm:p-6">
        <Card className="bg-white border-0 shadow">
          <CardHeader className="text-center pb-6 relative">
            <button
              onClick={() => setCurrentView("calculator")}
              className="absolute top-4 left-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <CardTitle className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-500 bg-clip-text text-transparent">
              {authMode === "login" ? "Sign In" : "Create Account"}
            </CardTitle>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {authMode === "login"
                ? "Welcome back! Please sign in to continue."
                : "Create your account to get started."}
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            {authMode === "login" ? (
              <LoginForm onSuccess={handleAuthSuccess} />
            ) : (
              <RegForm
                showRegisterButton={false}
                isBorrowForm={true}
                onSuccess={handleAuthSuccess}
              />
            )}

            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {authMode === "login"
                  ? "Don't have an account?"
                  : "Already have an account?"}
                <button
                  onClick={() =>
                    setAuthMode(authMode === "login" ? "register" : "login")
                  }
                  className="ml-1 text-primary-500 hover:text-primary-600 font-medium"
                >
                  {authMode === "login" ? "Sign up" : "Sign in"}
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Default Calculator View (keeping the rest of your component as is)
  return (
    <div className="w-full max-w-4xl mx-auto p-4 sm:p-6">
      <Card className="bg-gradient-to-br from-white to-gray-50/50 dark:from-gray-900 dark:to-gray-800/50 border-0 shadow-2xl">
        <CardHeader className="text-center pb-6 px-4 sm:px-6">
          <CardTitle className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-500 bg-clip-text text-transparent">
            Loan Calculator
          </CardTitle>
          <p className="text-gray-600 dark:text-gray-400 mt-2 text-base sm:text-lg">
            Quick loans from R500 to R2,000 with transparent fees
          </p>
        </CardHeader>

        <CardContent className="space-y-6 px-4 sm:px-6">
          {/* Loan Amount Input */}
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <label className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                Loan Amount (ZAR)
              </label>
              <div className="text-2xl font-bold text-primary-500">
                R{loanAmount.toLocaleString("en-ZA")}
              </div>
            </div>

            <div className="flex-1">
              <input
                type="number"
                value={loanAmount}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  if (inputValue === "") {
                    setLoanAmount("");
                  } else {
                    const numValue = parseInt(inputValue);
                    if (!isNaN(numValue)) {
                      setLoanAmount(numValue);
                    }
                  }
                }}
                onBlur={(e) => {
                  const value = parseInt(e.target.value);
                  if (isNaN(value) || value < 500) {
                    setLoanAmount(500);
                  } else if (value > 2000) {
                    setLoanAmount(2000);
                  }
                }}
                onFocus={(e) => e.target.select()}
                min="500"
                max="2000"
                step="50"
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-lg"
                placeholder="Enter amount (R500 - R2,000)"
              />
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Minimum: R500 • Maximum: R2,000
            </p>
          </div>

          {/* Payday Date Picker */}
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <label className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                When is your next payday?
              </label>
              <div className="text-2xl font-bold text-primary-500">
                {paydayDate ? `${calculateDaysToPayday()} days` : "Select date"}
              </div>
            </div>

            <div className="flex-1">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-lg justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {paydayDate
                      ? format(paydayDate, "PPP")
                      : "Select your next payday"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={paydayDate}
                    onSelect={setPaydayDate}
                    disabled={(date) => {
                      const today = new Date();
                      const maxDate = new Date();
                      maxDate.setDate(today.getDate() + 30);
                      return date < today || date > maxDate;
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Select a date within the next 30 days
            </p>
          </div>

          {isAmountValid && (
            <>
              {/* Simplified Loan Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* What You Receive */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-6 rounded-xl border border-green-200 dark:border-green-700">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-green-700 dark:text-green-300">
                        What You Receive
                      </h3>
                      <HoverCard>
                        <HoverCardTrigger asChild>
                          <button className="inline-flex items-center justify-center">
                            <svg
                              className="w-5 h-5 text-green-500 cursor-help hover:text-green-600 transition-colors"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </button>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-80 p-4">
                          <div className="space-y-3">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              Amount Breakdown
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-400">
                                  Loan Amount:
                                </span>
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  R
                                  {loanDetails.principal.toLocaleString(
                                    "en-ZA"
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-400">
                                  Raising Fee (5%):
                                </span>
                                <span className="font-medium text-red-600 dark:text-red-400">
                                  -R
                                  {loanDetails.raisingFee.toLocaleString(
                                    "en-ZA",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    }
                                  )}
                                </span>
                              </div>
                              <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                                <div className="flex justify-between items-center font-semibold">
                                  <span className="text-gray-900 dark:text-gray-100">
                                    You Receive:
                                  </span>
                                  <span className="text-green-600 dark:text-green-400">
                                    R
                                    {loanDetails.amountReceived.toLocaleString(
                                      "en-ZA",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                              The raising fee is deducted from your loan amount
                              before you receive the funds.
                            </p>
                          </div>
                        </HoverCardContent>
                      </HoverCard>
                    </div>
                    <p className="text-4xl font-bold text-green-600 dark:text-green-400">
                      R
                      {loanDetails.amountReceived.toLocaleString("en-ZA", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                  </div>
                </div>

                {/* Total Amount to Pay Back */}
                <div className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 p-6 rounded-xl border border-red-200 dark:border-red-700">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-red-700 dark:text-red-300">
                        Total Amount to Pay Back
                      </h3>
                      <HoverCard>
                        <HoverCardTrigger asChild>
                          <button className="inline-flex items-center justify-center">
                            <svg
                              className="w-5 h-5 text-red-500 cursor-help hover:text-red-600 transition-colors"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </button>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-80 p-4">
                          <div className="space-y-3">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              Payment Breakdown
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-400">
                                  Loan Amount:
                                </span>
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  R
                                  {loanDetails.principal.toLocaleString(
                                    "en-ZA"
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-400">
                                  Interest ({loanDetails.days} days):
                                </span>
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  R
                                  {loanDetails.interest.toLocaleString(
                                    "en-ZA",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    }
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-400">
                                  Initiation Fee:
                                </span>
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  R
                                  {loanDetails.initiationFee.toLocaleString(
                                    "en-ZA",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    }
                                  )}
                                </span>
                              </div>
                              <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                                <div className="flex justify-between items-center font-semibold">
                                  <span className="text-gray-900 dark:text-gray-100">
                                    Total:
                                  </span>
                                  <span className="text-red-600 dark:text-red-400">
                                    R
                                    {loanDetails.totalPayback.toLocaleString(
                                      "en-ZA",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </HoverCardContent>
                      </HoverCard>
                    </div>
                    <p className="text-4xl font-bold text-red-600 dark:text-red-400">
                      R
                      {loanDetails.totalPayback.toLocaleString("en-ZA", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                    <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                      Due on{" "}
                      {paydayDate
                        ? paydayDate.toLocaleDateString("en-ZA", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                        : "Select payday date"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Apply Button */}
              <div className="text-center">
                {loanAmount != 500 && (
                  <p>
                    Please make sure you are applying for an amount you can
                    afford
                  </p>
                )}
                <Button
                  onClick={handleBorrowToday}
                  disabled={isCreatingApplication}
                  className="bg-primary-500 hover:bg-primary-600 text-white px-8 py-4 text-lg font-semibold rounded-xl w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingApplication ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Creating Application...
                    </span>
                  ) : (
                    "Apply for Loan"
                  )}
                </Button>
              </div>
            </>
          )}

          {!isAmountValid && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
                Invalid Loan Amount
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Please enter a loan amount between R500 and R2,000.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoanCalculator;
